<template>
  <div class="prate" role="dialog" aria-modal="true" :aria-labelledby="titleId" @keydown.esc.prevent="emit('close')">
    <button type="button" class="prate__catch" aria-label="닫기" @click="emit('close')" />
    <div class="prate__panel" tabindex="-1" ref="panelEl">
      <header class="prate__head">
        <div>
          <h2 :id="titleId">평점</h2>
          <p class="prate__meta">
            <strong>{{ productName }}</strong>
            <span v-if="productCode">{{ productCode }}</span>
          </p>
        </div>
        <button type="button" class="prate__close" aria-label="평점 닫기" @click="emit('close')">
          <AppIcon icon="mdi-close" :size="20" />
        </button>
      </header>

      <section class="prate__score">
        <strong>{{ shownRating }}</strong>
        <div>
          <span class="prate__stars" :aria-label="`5점 만점에 ${shownRating}점`">
            <AppIcon
              v-for="star in 5"
              :key="star"
              :icon="star <= roundedStars ? 'mdi-star' : 'mdi-star-outline'"
              :size="18"
            />
          </span>
          <p>후기 {{ shownCount }}건</p>
        </div>
      </section>

      <ul v-if="loaded && reviews.length" class="prate__bars">
        <li v-for="row in buckets" :key="row.star">
          <span>{{ row.star }}점</span>
          <i><b :style="{ width: `${row.share}%` }" /></i>
          <em>{{ row.count }}</em>
        </li>
      </ul>
      <p v-else-if="!loaded" class="prate__note">후기를 집계하는 중입니다.</p>
      <p v-else-if="failed" class="prate__note">별점 분포는 지금 불러오지 못했습니다. 자세히 보기에서 후기를 확인할 수 있습니다.</p>
      <p v-else class="prate__note">아직 등록된 후기가 없습니다.</p>
      <p v-if="waiting" class="prate__wait">답글 없는 후기 {{ waiting }}건</p>

      <footer class="prate__actions">
        <button type="button" class="sui-btn sui-btn--ghost" @click="emit('close')">닫기</button>
        <button type="button" class="sui-btn sui-btn--primary" @click="openDetail">자세히 보기</button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchSellerReviews, type SellerMallReview } from '@/api/mallReview'

const props = defineProps<{
  productCode: string
  productName: string
  rating: number
  reviewCount: number
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const router = useRouter()
const titleId = `prate-${Math.random().toString(36).slice(2, 7)}`
const panelEl = ref<HTMLElement | null>(null)
const reviews = ref<SellerMallReview[]>([])
const loaded = ref(false)
const failed = ref(false)

const shownRating = computed(() => {
  if (loaded.value && reviews.value.length) {
    const sum = reviews.value.reduce((acc, review) => acc + Number(review.rating || 0), 0)
    return (sum / reviews.value.length).toFixed(1)
  }
  return Number(props.rating || 0).toFixed(1)
})

const shownCount = computed(() => (loaded.value ? reviews.value.length : Number(props.reviewCount || 0)))

const roundedStars = computed(() => Math.max(0, Math.min(5, Math.round(Number(shownRating.value) || 0))))

const buckets = computed(() => {
  const counts = [0, 0, 0, 0, 0]
  reviews.value.forEach((review) => {
    const star = Math.min(5, Math.max(1, Math.round(Number(review.rating) || 0)))
    counts[star - 1] += 1
  })
  const total = reviews.value.length || 1
  return [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: counts[star - 1],
    share: Math.round((counts[star - 1] / total) * 100),
  }))
})

const waiting = computed(() => reviews.value.filter((review) => !String(review.reply || '').trim()).length)

function openDetail() {
  router.push({
    path: '/seller/reviews',
    query: { tab: 'review', productCode: props.productCode },
  })
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close')
}

onMounted(async () => {
  document.addEventListener('keydown', onKey)
  await nextTick()
  panelEl.value?.focus()
  try {
    const all = await fetchSellerReviews()
    reviews.value = all.filter((review) => review.productCode === props.productCode)
  } catch {
    failed.value = true
  } finally {
    loaded.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKey)
})
</script>

<style lang="scss" src="./ProductRatingModal.scss"></style>
